import {useMemo, useState} from "react";
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField} from "@mui/material";
import {alpha} from "@mui/material/styles";
import {type GridColDef} from "@mui/x-data-grid";
import {useTranslation} from "react-i18next";
import {useAdminLegalEntities, useCreateLegalEntity} from "../../api/admin/adminQueries";
import AdminDataGrid from "./AdminDataGrid";
import SectionShell from "./AdminShared";
import {type LegalEntity, type LegalEntityDraft} from "./AdminTypes";

const emptyLegalEntities: LegalEntity[] = [];

const createInitialLegalEntityDraft = (): LegalEntityDraft => ({
    name: "",
    billingAddress: "",
    postalAddress: "",
    taxId: "",
});

const LegalEntityManagementTab = () => {
    const {t: translate} = useTranslation();
    const [isCreateLegalEntityOpen, setIsCreateLegalEntityOpen] = useState(false);
    const [legalEntityDraft, setLegalEntityDraft] = useState<LegalEntityDraft>(createInitialLegalEntityDraft);
    const legalEntitiesQuery = useAdminLegalEntities();
    const createLegalEntityMutation = useCreateLegalEntity();
    const legalEntities = legalEntitiesQuery.data ?? emptyLegalEntities;
    const pageErrorMessage = legalEntitiesQuery.error?.message;
    const createLegalEntityDialogErrorMessage = createLegalEntityMutation.error?.message;
    const legalEntityColumns = useMemo<GridColDef<LegalEntity>[]>(
        () => [
            {
                field: "name",
                headerName: translate("admin.shared.name"),
                flex: 1,
                minWidth: 220,
            },
            {
                field: "billingAddress",
                headerName: translate("admin.legalEntityManagement.columns.billingAddress"),
                flex: 1.2,
                minWidth: 260,
            },
            {
                field: "postalAddress",
                headerName: translate("admin.legalEntityManagement.columns.postalAddress"),
                flex: 1.1,
                minWidth: 240,
                valueGetter: (_value, row) => row.postalAddress || "—",
            },
            {
                field: "taxId",
                headerName: translate("admin.legalEntityManagement.columns.taxId"),
                flex: 0.8,
                minWidth: 180,
            },
        ],
        [translate],
    );

    const handleCreateLegalEntity = async () => {
        try {
            await createLegalEntityMutation.mutateAsync(legalEntityDraft);
            setLegalEntityDraft(createInitialLegalEntityDraft());
            setIsCreateLegalEntityOpen(false);
        } catch {
            return;
        }
    };

    const handleOpenCreateLegalEntity = () => {
        createLegalEntityMutation.reset();
        setIsCreateLegalEntityOpen(true);
    };

    const handleCloseCreateLegalEntity = () => {
        createLegalEntityMutation.reset();
        setIsCreateLegalEntityOpen(false);
    };

    return (
        <>
            <SectionShell
                eyebrow={translate("admin.legalEntityManagement.eyebrow")}
                title={translate("admin.legalEntityManagement.title")}
                action={
                    <Button
                        variant="contained"
                        onClick={handleOpenCreateLegalEntity}
                        sx={{
                            borderRadius: 999,
                            bgcolor: "primary.main",
                            fontWeight: 700,
                            boxShadow: "none",
                        }}
                    >
                        {translate("admin.legalEntityManagement.actions.createLegalEntity")}
                    </Button>
                }
            >
                <Stack spacing={3}>
                    <Alert
                        severity="info"
                        sx={(theme) => ({
                            borderRadius: 3,
                            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.08 : 0.16),
                            color: theme.palette.text.primary,
                            ".MuiAlert-icon": {color: theme.palette.primary.main},
                            alignItems: "center",
                        })}
                    >
                        {translate("admin.legalEntityManagement.description")}
                    </Alert>

                    {pageErrorMessage ? <Alert severity="error">{pageErrorMessage}</Alert> : null}

                    <AdminDataGrid
                        rows={legalEntities}
                        columns={legalEntityColumns}
                        autoHeight
                        loading={legalEntitiesQuery.isLoading || legalEntitiesQuery.isFetching}
                    />
                </Stack>
            </SectionShell>

            <Dialog open={isCreateLegalEntityOpen} onClose={handleCloseCreateLegalEntity} fullWidth maxWidth="sm">
                <DialogTitle>{translate("admin.legalEntityManagement.createDialog.title")}</DialogTitle>
                <DialogContent sx={{pt: 1}}>
                    <Stack spacing={2} sx={{mt: 1}}>
                        {createLegalEntityDialogErrorMessage ? (
                            <Alert severity="error">{createLegalEntityDialogErrorMessage}</Alert>
                        ) : null}
                        <TextField
                            label={translate("admin.shared.name")}
                            value={legalEntityDraft.name}
                            onChange={(event) =>
                                setLegalEntityDraft((previousDraft) => ({...previousDraft, name: event.target.value}))
                            }
                            fullWidth
                        />
                        <TextField
                            label={translate("admin.legalEntityManagement.columns.billingAddress")}
                            value={legalEntityDraft.billingAddress}
                            onChange={(event) =>
                                setLegalEntityDraft((previousDraft) => ({
                                    ...previousDraft,
                                    billingAddress: event.target.value,
                                }))
                            }
                            fullWidth
                        />
                        <TextField
                            label={translate("admin.legalEntityManagement.columns.postalAddress")}
                            value={legalEntityDraft.postalAddress}
                            onChange={(event) =>
                                setLegalEntityDraft((previousDraft) => ({
                                    ...previousDraft,
                                    postalAddress: event.target.value,
                                }))
                            }
                            fullWidth
                            helperText={translate("admin.legalEntityManagement.createDialog.optional")}
                        />
                        <TextField
                            label={translate("admin.legalEntityManagement.columns.taxId")}
                            value={legalEntityDraft.taxId}
                            onChange={(event) =>
                                setLegalEntityDraft((previousDraft) => ({...previousDraft, taxId: event.target.value}))
                            }
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2.5}}>
                    <Button variant="outlined" onClick={handleCloseCreateLegalEntity}>
                        {translate("common.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateLegalEntity}
                        disabled={createLegalEntityMutation.isPending}
                        sx={{bgcolor: "primary.main"}}
                    >
                        {translate("admin.legalEntityManagement.actions.createLegalEntity")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LegalEntityManagementTab;
